import { useRef } from 'react';
import Pusher, { type Channel as PusherChannel } from 'pusher-js';
import { useGetPusherChannelsClient } from './pusherChannels';
import type {
  Channel,
  ChannelEvent,
  ChannelEventData,
} from 'src/services/notificationService/channelsService/types';

type Event = ChannelEvent<Channel>;

/**
 * returns a function to bind a listener to a channel event
 */
export function useBindToChannelLazily() {
  const getClient = useGetPusherChannelsClient();
  /**
   * TL;DR; tracks which (event, listenerId) pairs have been bound — allows
   * one active binding per listenerId per event. The set deduplicates
   * concurrent binds caused by React StrictMode double-invocations or
   * rapid re-renders before the async getClient() resolves.
   *
   * We're supporting the tracking of multiple bound events here because
   * sometimes this ref is leveraged in a loop — see {@link useBindToChannelEvents}.
   */
  const boundEvents = useRef<Set<string>>(new Set());

  function boundKey(eventName: Event, listenerId: string) {
    return `${eventName}::${listenerId}`;
  }

  function setBound(eventName: Event, listenerId: string) {
    boundEvents.current?.add(boundKey(eventName, listenerId));
  }

  function setUnbound(eventName: Event, listenerId: string) {
    boundEvents.current?.delete(boundKey(eventName, listenerId));
  }

  function eventIsBound(eventName: Event, listenerId: string) {
    return boundEvents.current?.has(boundKey(eventName, listenerId));
  }

  /**
   * this ref is used to ensure the listener is always the latest version of the listener,
   * so it can reference changed values around it closure
   * @example
   *
   * bind(channel, event, () => {
   *  if (somePropInClosureScopeThatChanges) { // <= this prop will be the latest value so the logic will work
   *    // do something
   *  }
   * })
   */
  const eventListenerRef =
    useRef<
      (data: ChannelEventData<Channel, ChannelEvent<Channel>> | null) => void
    >();

  return function bindToChannel<
    C extends Channel,
    E extends ChannelEvent<C>,
    D extends ChannelEventData<C, E>
  >(
    channelName: Channel,
    {
      eventName,
      listener,
      enforceSingleListener,
      listenerId: providedListenerId,
    }: {
      eventName: E;
      listener: (data: D) => void;
      /**
       * if true, will unbind any existing listeners for this event, globally
       */
      enforceSingleListener?: boolean;
      /**
       * Stable ID for this binding, used to dedup concurrent bind attempts
       * and to identify which listener to unbind on cleanup.
       * Callers that need stable per-component dedup (useBindToChannelEvent)
       * should pass a UUID created with useRef.
       */
      listenerId?: string;
    }
  ) {
    let channel: PusherChannel | undefined;
    // Tracks whether the consumer has already called unbind() before the
    // async getClient() promise resolved. Without this, a fast unmount (or
    // dep-array change) would let the deferred bindToChannelsClient register
    // a Pusher listener with no owner — events fire forever, double-toasts /
    // double tick / stale-state callbacks. Setting this from unbind() makes
    // the deferred bind a no-op.
    let cancelled = false;

    // Resolve the effective listener identity: callers that care about
    // dedup (useBindToChannelEvent) pass an explicit listenerId. Legacy
    // callers that don't (useBindToEvents) fall back to the event name alone,
    // preserving the previous single-listener-per-event semantics.
    const effectiveListenerId = providedListenerId ?? eventName;

    // The function we hand to Pusher's channel.bind is a wrapper that
    // forwards through eventListenerRef so callers always get the latest
    // closure. We MUST keep the same wrapper reference for unbind, otherwise
    // pusher-js cannot match the registration and silently keeps it alive.
    const wrappedListener = (...args: [D]) =>
      eventListenerRef?.current?.(...args);

    const actions = {
      /**
       * see [docs](https://github.com/pusher/pusher-js#bind-and-unbind)
       */
      unbind: () => {
        // Tell any deferred bind path (getClient().then below) to bail.
        cancelled = true;
        if (enforceSingleListener) {
          // unbind every listener for this event
          channel?.unbind(eventName);
          setUnbound(eventName, effectiveListenerId);
          return;
        }
        // unbind just the wrapper we registered above. Passing the raw
        // `listener` arg here would not match (different function object)
        // and the Pusher binding would leak.
        channel?.unbind(eventName, wrappedListener);
        setUnbound(eventName, effectiveListenerId);
      },
    };

    // update the ref on every render, so its closure is aware of the latest values
    // @ts-expect-error - the listener type is narrowed above and will be safe at the call site
    eventListenerRef.current = listener;

    if (eventIsBound(eventName, effectiveListenerId)) {
      // already bound to event for this listener identity
      return actions;
    }

    function bindToChannelsClient(client: Pusher) {
      if (cancelled) {
        // Consumer's effect cleanup already fired. Do not register a
        // listener that has no owner.
        return;
      }
      // channel could be undefined? maybe because it's not subscribed to yet on a mount
      channel = client.channel(channelName);

      if (enforceSingleListener) {
        // only allow 1 listener per event per channel - globally
        channel?.unbind(eventName);
      }

      channel?.bind(eventName, wrappedListener);
      setBound(eventName, effectiveListenerId);
    }

    getClient().then((client) => {
      bindToChannelsClient(client);
    });

    return actions;
  };
}
